import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RefreshCw, Ban, CheckCircle, Trash2, Eye, ExternalLink, ShieldCheck, Lock, Plus, Users, User, ChevronRight, X, ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react';
import api from '../services/api';
import { useAuthStore } from '../stores/authStore';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import TopBar from '../components/layout/TopBar';
import Sidebar from '../components/layout/Sidebar';
import AgentDetailsDrawer, { type AgentDetailsTab } from '../features/agent-details/AgentDetailsDrawer';

// Helper: check if user was created in last 14 days
const isNewUser = (createdAt?: string): boolean => {
  if (!createdAt) return false;
  const created = new Date(createdAt);
  const twoWeeksAgo = new Date();
  twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
  return created >= twoWeeksAgo;
};

type TabId =
  | 'agents'
  | 'knowledge'
  | 'schedules'
  | 'analytics'
  | 'users'
  | 'groups'
  | 'slack'
  | 'account';

interface PrivacyInfo {
  retention: { key: string; label: string; detail: string; days: number }[];
  privacy_policy_url: string | null;
  privacy_policy_label: string;
}

// email = whose history to delete; null = the signed-in user's own.
interface DeleteTarget {
  email: string | null;
}

interface RoleInfo {
  email: string;
  is_admin: boolean;
  role: string;
  can_manage_agents?: boolean;
  managed_groups?: string[];
}

interface Agent {
  task_name: string;
  display_name: string;
  description?: string;
  is_default?: boolean;
  bq_project: string;
  bq_dataset: string;
  bq_tables: string[];
  table_count: number;
  schedule: string;
  slack_channel?: string;
  slack_channel_id?: string;
  slack_vertical?: string[];
  managed_by_yaml?: boolean;
  allowed_users?: string[];
  digest_enabled?: boolean;
  context?: string;
  questions?: string[];
  premade_queries?: any[];
  column_filters?: Record<string, string[]>;
  column_value_filters?: Record<string, any[]>;
  can_view_details?: boolean;
}

interface AppUser {
  email: string;
  role: string;
  first_name?: string;
  last_name?: string;
  last_login_at?: string;
  is_active?: boolean;
  deactivated_at?: string;
  created_at?: string;
}

// Per-user permissions (GET /api/users/access); access is computed server-side.
interface UserAccess {
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  role: string | null; // admins only
  is_active?: boolean;
  groups: { name: string; is_admin: boolean }[];
  all_agents: boolean;
  agents: { task_name: string; display_name: string; via: string[] }[];
}

const ROLE_LABELS: Record<string, string> = { admin: 'Admin', agent_manager: 'Agent manager', viewer: 'Viewer' };

function AccessUserName({ u }: { u: UserAccess }) {
  const name = u.first_name && u.last_name ? `${u.first_name} ${u.last_name}` : u.email.split('@')[0];
  const tag: React.CSSProperties = {
    fontSize: '11px',
    padding: '1px 6px',
    borderRadius: '999px',
    border: '1px solid var(--border)',
    color: 'var(--text-muted)',
    whiteSpace: 'nowrap',
  };
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600, fontSize: '13px' }}>{name}</span>
        {u.role && <span style={{ ...tag, color: u.role === 'admin' ? 'var(--accent)' : 'var(--text-muted)' }}>{ROLE_LABELS[u.role] || u.role}</span>}
        {u.is_active === false && <span style={tag}>Deactivated</span>}
      </div>
      <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '2px' }}>{u.email}</div>
    </div>
  );
}

interface SlackChannel {
  id: string;
  name?: string;
  label?: string;
  enabled?: boolean;
}

interface KnowledgeDoc {
  name: string;
  filename?: string;
  description?: string;
  content: string;
  legacy?: boolean;
}

// filename is empty for a section that hasn't been created yet.
interface KnowledgeDraft {
  filename: string;
  name: string;
  description: string;
  content: string;
  legacy: boolean;
}

interface KnowledgeAgent {
  task_name: string;
  display_name: string;
  context?: string;
  tables?: string[];
  questions?: string[];
}

const TABS: { id: TabId; label: string; adminOnly?: boolean }[] = [
  { id: 'agents', label: 'Data Agents' },
  { id: 'knowledge', label: 'Knowledge' },
  { id: 'schedules', label: 'Schedules' },
  { id: 'analytics', label: 'Analytics', adminOnly: true },
  { id: 'users', label: 'Users', adminOnly: true },
  { id: 'groups', label: 'Permission Groups', adminOnly: true },
  { id: 'slack', label: 'Slack Tools', adminOnly: true },
  { id: 'account', label: 'Account & Privacy' },
];

const FAIL_CHANNEL = 'C0AM10CD03Y';

const panelTitle: React.CSSProperties = {
  margin: '0 0 6px 0',
  fontSize: '16px',
  fontWeight: 700,
};

const panelSub: React.CSSProperties = {
  margin: '0 0 18px 0',
  fontSize: '13px',
  color: 'var(--text-muted)',
  lineHeight: 1.5,
};

const cardStyle: React.CSSProperties = {
  padding: '14px 16px',
  background: 'var(--surface2)',
  border: '1px solid var(--border)',
  borderRadius: '10px',
  marginBottom: '10px',
};

const btnPrimary: React.CSSProperties = {
  padding: '8px 14px',
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const btnGhost: React.CSSProperties = {
  padding: '7px 12px',
  background: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--border)',
  borderRadius: '8px',
  fontSize: '12px',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const btnDanger: React.CSSProperties = {
  ...btnGhost,
  color: 'var(--red)',
  borderColor: 'var(--border)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  background: 'var(--surface2)',
  border: '1px solid var(--border)',
  borderRadius: '8px',
  color: 'var(--text)',
  fontSize: '13px',
  fontFamily: 'inherit',
  outline: 'none',
};

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [tab, setTab] = useState<TabId>('agents');
  const [role, setRole] = useState<RoleInfo | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [users, setUsers] = useState<AppUser[]>([]);
  const [groups, setGroups] = useState<{ name: string; description: string; members: string[]; admins: string[] }[]>([]);
  const [channels, setChannels] = useState<SlackChannel[]>([]);
  const [knowledge, setKnowledge] = useState<{ global_docs: KnowledgeDoc[]; agents: KnowledgeAgent[] }>({ global_docs: [], agents: [] });
  const [knowledgeQuery, setKnowledgeQuery] = useState('');
  const [knowledgeSection, setKnowledgeSection] = useState<'business' | 'agents'>('business');
  const [expandedKnowledge, setExpandedKnowledge] = useState<Set<string>>(new Set());
  const [editingKnowledge, setEditingKnowledge] = useState<KnowledgeDraft | null>(null);
  const [confirmDeleteKnowledge, setConfirmDeleteKnowledge] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState('viewer');
  const [slackChannelId, setSlackChannelId] = useState('');
  const [slackMsgUrl, setSlackMsgUrl] = useState('');
  const [busy, setBusy] = useState('');
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<{ name: string; description: string; members: string[]; admins: string[] } | null>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  // ?agent=<task_name> opens the read-only details drawer.
  const [searchParams, setSearchParams] = useSearchParams();
  const [drawerTab, setDrawerTab] = useState<AgentDetailsTab>('overview');
  const [confirmDeleteFor, setConfirmDeleteFor] = useState<string | null>(null);
  const [privacy, setPrivacy] = useState<PrivacyInfo | null>(null);
  const [privacyError, setPrivacyError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleteResult, setDeleteResult] = useState('');
  const [groupsView, setGroupsView] = useState<'groups' | 'users'>('groups'); // opens on groups
  const [accessSort, setAccessSort] = useState<{ key: 'user' | 'groups' | 'access'; dir: 'asc' | 'desc' }>({ key: 'user', dir: 'asc' });
  const [access, setAccess] = useState<UserAccess[] | null>(null);
  const [accessError, setAccessError] = useState('');
  const [accessQuery, setAccessQuery] = useState('');
  const [accessUser, setAccessUser] = useState<string | null>(null); // email whose access panel is open
  const [addGroups, setAddGroups] = useState<Set<string> | null>(null); // "Add to groups" picker; null = closed
  const [addGroupsBusy, setAddGroupsBusy] = useState(false);
  const [addGroupsError, setAddGroupsError] = useState('');



  const isAdmin = !!role?.is_admin;
  const canManageAgents = !!(role?.can_manage_agents || role?.is_admin);
  const managesGroups = (role?.managed_groups?.length || 0) > 0;
  // Group admins see Permission Groups, limited server-side to the groups they manage.
  const visibleTabs = TABS.filter((t) => !t.adminOnly || isAdmin || (t.id === 'groups' && managesGroups));

  // Helper to extract clean error messages from API responses
  const extractErrorMessage = (err: any, fallback: string): string => {
    // Check for detail string (HTTPException)
    if (err?.response?.data?.detail && typeof err.response.data.detail === 'string') {
      return err.response.data.detail;
    }
    // Check for 422 validation errors (array of objects)
    if (Array.isArray(err?.response?.data)) {
      const firstError = err.response.data[0];
      if (firstError?.msg) {
        return firstError.msg;
      }
    }
    // Fallback to generic error message
    return err?.message || fallback;
  };

  const loadRole = useCallback(async () => {
    const res = await api.get('/api/me/role');
    setRole(res.data);
    return res.data as RoleInfo;
  }, []);

  const loadAgents = useCallback(async () => {
    const res = await api.get('/api/datasets');
    setAgents(res.data.agents || []);
    if (typeof res.data.is_admin === 'boolean') {
      setRole((prev) => (prev ? { ...prev, is_admin: res.data.is_admin } : prev));
    }
  }, []);

  const loadUsers = useCallback(async () => {
    const res = await api.get('/api/users');
    setUsers(res.data.users || []);
  }, []);

  const loadGroups = useCallback(async () => {
    const res = await api.get('/api/groups');
    const obj = res.data.groups || {};
    setGroups(
      Object.entries(obj).map(([name, data]: [string, any]) => ({
        name,
        description: data.description || '',
        members: data.members || [],
        admins: data.admins || [],
      }))
    );
  }, []);


  const loadKnowledge = useCallback(async () => {
    const res = await api.get('/api/knowledge');
    setKnowledge({
      global_docs: res.data.global_docs || [],
      agents: res.data.agents || [],
    });
  }, []);

  const loadSlack = useCallback(async () => {
    const res = await api.get('/api/slack/channels');
    setChannels(res.data.channels || []);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const saved = localStorage.getItem('fm-theme') as 'dark' | 'light' | null;
    if (saved) {
      setTheme(saved);
      document.documentElement.setAttribute('data-theme', saved);
    }
    (async () => {
      setLoading(true);
      setError('');
      setNotice('');
      try {
        await loadRole();
        if (cancelled) return;
        await Promise.all([loadAgents(), loadUsers(), loadGroups()]);
      } catch (err: any) {
        if (!cancelled) setError(extractErrorMessage(err, 'Failed to load settings'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadRole, loadAgents, loadUsers, loadGroups]);

  useEffect(() => {
    if ((tab === 'slack' || tab === 'schedules') && isAdmin) {
      loadSlack().catch(() => setError('Could not load Slack channels'));
    }
    if (tab === 'knowledge') {
      loadKnowledge().catch(() => setError('Could not load knowledge'));
    }
    if (tab === 'account' && !privacy) {
      setPrivacyError('');
      api.get('/api/privacy')
        .then((res) => setPrivacy(res.data))
        .catch(() => setPrivacyError('Could not load the privacy settings. Please refresh the page.'));
    }
  }, [tab, isAdmin, loadSlack, loadKnowledge, privacy]);

  useEffect(() => {
    setAddGroups(null);
    setAddGroupsError('');
  }, [accessUser]);

  useEffect(() => {
    if (!accessUser) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAccessUser(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [accessUser]);

  useEffect(() => {
    if (tab !== 'groups' || groupsView !== 'users' || access) return;
    setAccessError('');
    api.get('/api/users/access')
      .then((res) => setAccess(res.data.users || []))
      .catch((err) => setAccessError(extractErrorMessage(err, 'Could not load user access. Please try again.')));
  }, [tab, groupsView, access]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (openMenu) setOpenMenu(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openMenu]);

  const runDigest = async (taskName: string) => {
    setBusy(`run:${taskName}`);
    setError('');
    setNotice('');
    try {
      const res = await api.post(`/run/${encodeURIComponent(taskName)}`);
      setNotice(res.data?.ok === false
        ? (res.data.error || 'Run finished with warnings — check Slack.')
        : `Digest triggered for ${taskName}. Check Slack in a few minutes.`);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Run failed'));
    } finally {
      setBusy('');
    }
  };

  // The API decides who may see an agent's configuration (can_view_details).
  const viewingAgent = agents.find((a) => a.can_view_details && a.task_name === searchParams.get('agent'));

  const openAgent = (taskName: string, opts: { tab?: AgentDetailsTab; confirmDelete?: boolean } = {}) => {
    setOpenMenu(null);
    setDrawerTab(opts.tab || 'overview');
    setConfirmDeleteFor(opts.confirmDelete ? taskName : null);
    setSearchParams({ agent: taskName }, { replace: true });
  };

  const closeAgent = () => {
    setConfirmDeleteFor(null);
    setSearchParams({}, { replace: true });
  };

  // Called from the drawer's inline confirmation; returns an error message or null.
  const deleteAgent = async (agent: Agent): Promise<string | null> => {
    try {
      await api.delete(`/api/datasets/${encodeURIComponent(agent.task_name)}`);
    } catch (err: any) {
      return extractErrorMessage(err, 'Could not delete the agent. Please try again.');
    }
    closeAgent();
    setNotice(`Deleted ${agent.display_name}`);
    await loadAgents().catch(() => setError('Could not reload agents'));
    return null;
  };

  const duplicateAgent = (agent: Agent) => {
    setOpenMenu(null);
    
    // Create a copy with modified display name and cleared Slack settings
    const duplicatedAgent = {
      ...agent,
      task_name: '', // Will be auto-generated from display_name
      display_name: `${agent.display_name} (Copy)`,
      digest_enabled: false, // Turn off Slack digest
      slack_channel: '', // Clear Slack channel - user will select
      slack_channel_id: '', // Clear Slack channel ID
      schedule: '', // Clear schedule
      // Copied queries aren't verified for the new tables until tested again
      premade_queries: (agent.premade_queries || []).map((q: any) => ({ ...q, verified: false })),
      // Keep all other fields including context, tables, etc.
    };
    
    // Navigate to add agent page (not edit) with pre-populated data
    navigate('/settings/addagent', { state: { editAgent: duplicatedAgent } });
  };

  const addUser = async () => {
    const email = newUserEmail.trim().toLowerCase();
    if (!email) return;
    setBusy('user-add');
    setError('');
    try {
      await api.put(`/api/users/${encodeURIComponent(email)}`, { role: newUserRole });
      setNewUserEmail('');
      setNotice(`Added ${email}`);
      await loadUsers();
      // Refresh current user's role in case they modified themselves
      try {
        await loadRole();
      } catch (roleErr) {
        // Non-fatal: user was added successfully, just couldn't refresh our own role
        console.warn('Could not refresh current user role:', roleErr);
      }
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Could not add user'));
    } finally {
      setBusy('');
    }
  };

  const setUserRole = async (email: string, nextRole: string, currentRole: string) => {
    const roleLabel = (r: string) => r === 'admin' ? 'Admin' : r === 'agent_manager' ? 'Agent Manager' : 'Viewer';
    if (!window.confirm(`Change ${email}'s role from ${roleLabel(currentRole)} to ${roleLabel(nextRole)}?`)) return;
    setBusy(`role:${email}`);
    try {
      await api.put(`/api/users/${encodeURIComponent(email)}`, { role: nextRole });
      await loadUsers();
      // Refresh current user's role in case they modified themselves
      try {
        await loadRole();
      } catch (roleErr) {
        // Non-fatal: role was updated successfully, just couldn't refresh our own role
        console.warn('Could not refresh current user role:', roleErr);
      }
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Could not update role'));
    } finally {
      setBusy('');
    }
  };

  const removeUser = async (email: string) => {
    if (!window.confirm(`Remove access for ${email}?`)) return;
    setBusy(`rm:${email}`);
    try {
      await api.delete(`/api/users/${encodeURIComponent(email)}`);
      await loadUsers();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Could not remove user'));
    } finally {
      setBusy('');
    }
  };

  const openGroupEditor = (group: { name: string; description: string; members: string[]; admins: string[] } | null) => {
    setEditingGroup(group || { name: '', description: '', members: [], admins: [] });
    setShowGroupModal(true);
    setError('');
  };

  const saveGroup = async (groupData: { name: string; description: string; members: string[]; admins: string[] }, isNew: boolean, originalName?: string) => {
    if (!groupData.name.trim()) {
      setError('Group name is required');
      return;
    }

    setBusy('save-group');
    setError('');
    try {
      // If renaming, delete old group first
      if (!isNew && originalName && originalName !== groupData.name) {
        await api.delete(`/api/groups/${encodeURIComponent(originalName)}`);
      }

      // Create or update group
      await api.put(`/api/groups/${encodeURIComponent(groupData.name)}`, {
        description: groupData.description,
        members: groupData.members,
        admins: groupData.admins
      });

      setShowGroupModal(false);
      setEditingGroup(null);
      await loadGroups();
      setAccess(null);
      setNotice(isNew ? 'Group created' : 'Group updated');
      setTimeout(() => setNotice(''), 3000);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Could not save group'));
    } finally {
      setBusy('');
    }
  };

  const deleteGroup = async (groupName: string) => {
    if (!window.confirm(`Delete group "${groupName}"? Tasks referencing group:${groupName} will lose this grant.`)) return;

    setBusy('delete-group');
    setError('');
    try {
      await api.delete(`/api/groups/${encodeURIComponent(groupName)}`);
      setShowGroupModal(false);
      setEditingGroup(null);
      await loadGroups();
      setAccess(null);
      setNotice('Group deleted');
      setTimeout(() => setNotice(''), 3000);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Could not delete group'));
    } finally {
      setBusy('');
    }
  };

  const addSlackChannel = async () => {
    const id = slackChannelId.trim();
    if (!id) return;
    setBusy('slack-add');
    setError('');
    try {
      const res = await api.post('/api/slack/channels', { id });
      setSlackChannelId('');
      setNotice(`Added #${res.data.channel?.name || id}`);
      setTimeout(() => setNotice(''), 3000);
      await loadSlack();
    } catch (err: any) {
      // Try to extract error message, with status-specific fallbacks
      let message = extractErrorMessage(err, '');
      if (!message) {
        message = (err?.response?.status === 422 ? 'Could not access channel. Make sure:\n• The channel ID is correct\n• Pearson bot is invited to the channel' : null)
        || (err?.response?.status === 500 ? 'Server error while adding channel. Please try again.' : null)
        || (err?.response?.status === 403 ? 'You do not have permission to add channels.' : null)
        || 'Could not add channel. Please check the channel ID and try again.';
      }
      setError(message);
    } finally {
      setBusy('');
    }
  };

  const removeSlackChannel = async (id: string) => {
    if (!window.confirm(`Remove channel ${id} from the allowlist?`)) return;
    setBusy(`slack-rm:${id}`);
    try {
      await api.delete(`/api/slack/channels/${encodeURIComponent(id)}`);
      await loadSlack();
    } catch (err: any) {
      let message = extractErrorMessage(err, '');
      if (!message) {
        message = (err?.response?.status === 422 ? 'Cannot remove this channel.' : null)
        || (err?.response?.status === 500 ? 'Server error while removing channel. Please try again.' : null)
        || (err?.response?.status === 403 ? 'You do not have permission to remove channels.' : null)
        || 'Could not remove channel. Please try again.';
      }
      setError(message);
    } finally {
      setBusy('');
    }
  };

  const toggleSlackChannel = async (id: string, currentlyEnabled: boolean) => {
    const newState = !currentlyEnabled;
    setBusy(`slack-toggle:${id}`);
    setError('');
    try {
      await api.patch(`/api/slack/channels/${encodeURIComponent(id)}`, { enabled: newState });
      await loadSlack();
      setNotice(newState ? `Channel enabled` : `Channel disabled (maintenance mode)`);
      setTimeout(() => setNotice(''), 3000);
    } catch (err: any) {
      let message = extractErrorMessage(err, '');
      if (!message) {
        message = (err?.response?.status === 422 ? 'Cannot modify this channel.' : null)
        || (err?.response?.status === 500 ? 'Server error. Please try again.' : null)
        || 'Could not update channel. Please try again.';
      }
      setError(message);
    } finally {
      setBusy('');
    }
  };

  const refreshAllChannels = async () => {
    setBusy('slack-refresh');
    setError('');
    setNotice('');
    try {
      const res = await api.post('/api/slack/channels/refresh');
      const { updated, failed, total, errors } = res.data;
      
      if (failed > 0) {
        const errorDetails = errors.map((e: any) => `${e.name} (${e.id}): ${e.error}`).join('; ');
        setNotice(`Refreshed ${updated}/${total} channels. ${failed} failed: ${errorDetails}`);
      } else if (updated > 0) {
        setNotice(`✓ Refreshed ${updated} channel${updated !== 1 ? 's' : ''} from Slack`);
      } else {
        setNotice('All channels are up to date');
      }
      setTimeout(() => setNotice(''), 3000);
      
      await loadSlack();
    } catch (err: any) {
      let message = extractErrorMessage(err, '');
      if (!message) {
        message = (err?.response?.status === 500 ? 'Server error while refreshing channels. Please try again.' : null)
        || (err?.response?.status === 403 ? 'You do not have permission to refresh channels.' : null)
        || 'Could not refresh channels. Please check your connection and try again.';
      }
      setError(message);
    } finally {
      setBusy('');
    }
  };

  const deleteSlackMessage = async () => {
    const url = slackMsgUrl.trim();
    if (!url) return;
    setBusy('slack-del-msg');
    setError('');
    try {
      const res = await api.post('/api/slack/delete-message', { url });
      if (res.data?.ok) {
        setNotice(`Message deleted (${res.data.channel} / ${res.data.ts})`);
        setTimeout(() => setNotice(''), 3000);
        setSlackMsgUrl('');
      } else {
        setError(res.data?.error || 'Delete failed');
      }
    } catch (err: any) {
      let message = extractErrorMessage(err, '');
      if (!message) {
        message = (err?.response?.status === 422 ? 'Invalid Slack message URL. Please copy the link from Slack.' : null)
        || (err?.response?.status === 500 ? 'Server error while deleting message. Please try again.' : null)
        || (err?.response?.status === 403 ? 'You do not have permission to delete messages.' : null)
        || 'Could not delete message. Please check the URL and try again.';
      }
      setError(message);
    } finally {
      setBusy('');
    }
  };

  const saveKnowledge = async (draft: KnowledgeDraft) => {
    setBusy('save-knowledge');
    setError('');
    try {
      if (!draft.filename) {
        await api.post('/api/knowledge', {
          name: draft.name,
          description: draft.description,
          content: draft.content,
        });
      } else {
        await api.put('/api/knowledge', {
          filename: draft.filename,
          content: draft.content,
          ...(draft.legacy ? {} : { name: draft.name, description: draft.description }),
        });
      }
      setNotice(`Saved ${draft.name}`);
      setEditingKnowledge(null);
      await loadKnowledge();
      setTimeout(() => setNotice(''), 3000);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Could not save knowledge'));
    } finally {
      setBusy('');
    }
  };

  const deleteKnowledge = async (doc: KnowledgeDoc) => {
    if (!doc.filename) return;
    setBusy(`delete-knowledge-${doc.filename}`);
    setError('');
    try {
      await api.delete(`/api/knowledge/${encodeURIComponent(doc.filename)}`);
      setConfirmDeleteKnowledge(null);
      setNotice(`Deleted ${doc.name}`);
      await loadKnowledge();
      setTimeout(() => setNotice(''), 3000);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Could not delete section'));
    } finally {
      setBusy('');
    }
  };

  const toggleTheme = () => {
    const next = theme === 'light' ? 'dark' : 'light';
    setTheme(next);
    localStorage.setItem('fm-theme', next);
    document.documentElement.setAttribute('data-theme', next);
  };

  const q = knowledgeQuery.trim().toLowerCase();
  const filteredDocs = knowledge.global_docs.filter(
    (d) => !q || `${d.name} ${d.content}`.toLowerCase().includes(q)
  );
  const filteredKnowledgeAgents = knowledge.agents.filter(
    (a) =>
      !q ||
      [a.display_name, a.context, ...(a.tables || []), ...(a.questions || [])]
        .join(' ')
        .toLowerCase()
        .includes(q)
  );

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--bg)' }}>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)}>
          <nav style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}>
            {visibleTabs.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => { setTab(t.id); setError(''); setNotice(''); }}
                style={{
                  textAlign: 'left',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: 'none',
                  background: tab === t.id ? 'var(--accent)' : 'transparent',
                  color: tab === t.id ? '#fff' : 'var(--text)',
                  fontSize: '13px',
                  fontWeight: tab === t.id ? 600 : 500,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  transition: 'all 0.2s',
                }}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </Sidebar>

      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <TopBar
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen(true)}
          theme={theme}
          onToggleTheme={toggleTheme}
          user={user}
        />

        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px' }}>
          <h1 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: 700 }}>
            Settings
          </h1>
          {loading && (
            <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Loading…</div>
          )}
          {error && (
            <div style={{
              position: 'fixed',
              top: '20px',
              right: '20px',
              zIndex: 9999,
              maxWidth: '400px',
              padding: '12px 16px',
              borderRadius: '8px',
              background: 'rgba(239,68,68,0.95)',
              color: '#fff',
              fontSize: '13px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              backdropFilter: 'blur(8px)'
            }}>
              {typeof error === 'string' ? error : JSON.stringify(error)}
            </div>
          )}
          {notice && (
            <div style={{
              position: 'fixed',
              top: '20px',
              right: '20px',
              zIndex: 9999,
              maxWidth: '400px',
              padding: '12px 16px',
              borderRadius: '8px',
              background: 'rgba(34,197,94,0.95)',
              color: '#fff',
              fontSize: '13px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              backdropFilter: 'blur(8px)'
            }}>
              {notice}
            </div>
          )}

          {!loading && tab === 'agents' && (
            <div>
              <h3 style={panelTitle}>Data Agents</h3>
              <p style={panelSub}>
                Each Data Agent is scoped to BigQuery tables, with its own context, seed questions and digest schedule.
              </p>
              
              {/* Add Data Agent Button */}
              {canManageAgents && (
                <button
                  type="button"
                  style={{ ...btnPrimary, marginBottom: '20px' }}
                  onClick={() => navigate('/settings/addagent')}
                >
                  + Add Data Agent
                </button>
              )}

              {agents.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>No Data Agents configured yet.</div>
              ) : (
                agents.map((a) => (
                  <div
                    key={a.task_name}
                    role={a.can_view_details ? 'button' : undefined}
                    tabIndex={a.can_view_details ? 0 : undefined}
                    style={{ ...cardStyle, cursor: a.can_view_details ? 'pointer' : 'default' }}
                    onClick={() => { if (a.can_view_details) openAgent(a.task_name); }}
                    onKeyDown={(e) => {
                      if (a.can_view_details && e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        openAgent(a.task_name);
                      }
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start' }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: '14px' }}>{a.display_name}</div>
                        {a.description && (
                          <div style={{
                            fontSize: '13px',
                            color: 'var(--text-muted)',
                            marginTop: '8px',
                            lineHeight: 1.4
                          }}>
                            {a.description}
                          </div>
                        )}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>
                          <Chip>{a.table_count} table{a.table_count === 1 ? '' : 's'}</Chip>
                          <Chip>cron {a.schedule || '—'}</Chip>
                          {a.slack_channel && <Chip>#{a.slack_channel.replace(/^#/, '')}</Chip>}
                          {!a.managed_by_yaml && <Chip>auto-discovered</Chip>}
                        </div>
                      </div>
                      {canManageAgents && a.managed_by_yaml && (
                        <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenMenu(openMenu === a.task_name ? null : a.task_name);
                            }}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              padding: '4px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'var(--text-muted)',
                              borderRadius: '6px',
                              transition: 'all 0.15s'
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.background = 'var(--surface)';
                              e.currentTarget.style.color = 'var(--text)';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.background = 'transparent';
                              e.currentTarget.style.color = 'var(--text-muted)';
                            }}
                          >
                            <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: '20px', height: '20px' }}>
                              <circle cx="12" cy="5" r="1.5"/>
                              <circle cx="12" cy="12" r="1.5"/>
                              <circle cx="12" cy="19" r="1.5"/>
                            </svg>
                          </button>

                          {/* Dropdown Menu */}
                          {openMenu === a.task_name && (
                            <div
                              style={{
                                position: 'absolute',
                                right: 0,
                                top: '32px',
                                background: 'var(--surface)',
                                border: '1px solid var(--border)',
                                borderRadius: '8px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                minWidth: '160px',
                                zIndex: 1000,
                                overflow: 'hidden'
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                onClick={() => openAgent(a.task_name)}
                                style={{
                                  width: '100%',
                                  padding: '10px 16px',
                                  background: 'transparent',
                                  border: 'none',
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  color: 'var(--text)',
                                  fontSize: '14px',
                                  transition: 'background 0.15s',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px'
                                }}
                                onMouseOver={(e) => { e.currentTarget.style.background = 'var(--surface)'; }}
                                onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; }}
                              >
                                <Eye size={16} />
                                View details
                              </button>
                              <button
                                onClick={() => {
                                  setOpenMenu(null);
                                  navigate('/settings/editagent', { state: { editAgent: a } });
                                }}
                                style={{
                                  width: '100%',
                                  padding: '10px 16px',
                                  background: 'transparent',
                                  border: 'none',
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  color: 'var(--text)',
                                  fontSize: '14px',
                                  transition: 'background 0.15s',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px'
                                }}
                                onMouseOver={(e) => { e.currentTarget.style.background = 'var(--surface)'; }}
                                onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; }}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px' }}>
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                </svg>
                                Edit agent
                              </button>
                              <button
                                onClick={() => duplicateAgent(a)}
                                style={{
                                  width: '100%',
                                  padding: '10px 16px',
                                  background: 'transparent',
                                  border: 'none',
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  color: 'var(--text)',
                                  fontSize: '14px',
                                  transition: 'background 0.15s',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px'
                                }}
                                onMouseOver={(e) => { e.currentTarget.style.background = 'var(--surface)'; }}
                                onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; }}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px' }}>
                                  <rect x="9" y="9" width="13" height="13" rx="2"/>
                                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                                </svg>
                                Duplicate agent
                              </button>
                              <div style={{ height: '1px', background: 'var(--border)', margin: '0' }}></div>
                              <button
                                onClick={() => openAgent(a.task_name, { confirmDelete: true })}
                                style={{
                                  width: '100%',
                                  padding: '10px 16px',
                                  background: 'transparent',
                                  border: 'none',
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  color: 'var(--red)',
                                  fontSize: '14px',
                                  transition: 'background 0.15s',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px'
                                }}
                                onMouseOver={(e) => { e.currentTarget.style.background = 'var(--surface)'; }}
                                onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; }}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px' }}>
                                  <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                </svg>
                                Delete agent
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {!loading && tab === 'knowledge' && (
            <div>
              <h3 style={panelTitle}>Knowledge Base</h3>
              <p style={panelSub}>
                Business rules and agent instructions. Changes reach agents within a minute.
              </p>

              {/* Section Toggle */}
              <div style={{ 
                display: 'flex', 
                gap: '8px', 
                marginBottom: '16px',
                padding: '4px',
                background: 'var(--surface2)',
                borderRadius: '10px',
                width: 'fit-content'
              }}>
                <button
                  type="button"
                  onClick={() => setKnowledgeSection('business')}
                  style={{
                    padding: '8px 16px',
                    background: knowledgeSection === 'business' ? 'var(--accent)' : 'transparent',
                    color: knowledgeSection === 'business' ? '#fff' : 'var(--text)',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    fontFamily: 'inherit'
                  }}
                >
                  Business Rules
                </button>
                <button
                  type="button"
                  onClick={() => setKnowledgeSection('agents')}
                  style={{
                    padding: '8px 16px',
                    background: knowledgeSection === 'agents' ? 'var(--accent)' : 'transparent',
                    color: knowledgeSection === 'agents' ? '#fff' : 'var(--text)',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    fontFamily: 'inherit'
                  }}
                >
                  Agent Context
                </button>
              </div>

              <input
                style={{ ...inputStyle, marginBottom: '16px' }}
                value={knowledgeQuery}
                onChange={(e) => setKnowledgeQuery(e.target.value)}
                placeholder="Search knowledge..."
              />
              
              {/* Business Rules Section */}
              {knowledgeSection === 'business' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: 0 }}>
                      Core business rules used by all agents. Admins can update these definitions or add new sections.
                    </p>
                    {isAdmin && (
                      <button
                        type="button"
                        style={{ ...btnPrimary, display: 'inline-flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}
                        onClick={() => {
                          setError('');
                          setEditingKnowledge({ filename: '', name: '', description: '', content: '', legacy: false });
                        }}
                      >
                        <Plus size={14} />
                        New section
                      </button>
                    )}
                  </div>

                  {filteredDocs.length === 0 && (
                    <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>No knowledge files found.</div>
                  )}
                  
                  {filteredDocs.map((doc) => {
                    const isExpanded = expandedKnowledge.has(doc.name);
                    const lineCount = doc.content.split('\n').length;
                    
                    return (
                      <div key={doc.filename || doc.name} style={{
                        ...cardStyle, 
                        overflow: 'hidden',
                        transition: 'all 0.2s'
                      }}>
                        {/* Header - Always Visible */}
                        <div 
                          style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}
                          onClick={() => {
                            const newExpanded = new Set(expandedKnowledge);
                            if (isExpanded) {
                              newExpanded.delete(doc.name);
                            } else {
                              newExpanded.add(doc.name);
                            }
                            setExpandedKnowledge(newExpanded);
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                              fontWeight: 600, 
                              fontSize: '14px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              <svg 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                style={{ 
                                  width: '14px', 
                                  height: '14px',
                                  color: 'var(--text-muted)',
                                  transition: 'transform 0.2s',
                                  transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                }}
                              >
                                <path d="M9 18l6-6-6-6"/>
                              </svg>
                              {doc.name}
                            </div>
                            <div style={{ 
                              fontSize: '11px', 
                              color: 'var(--text-muted)',
                              marginLeft: '22px'
                            }}>
                              {lineCount} lines{doc.legacy ? ' · Built-in' : ''}
                            </div>
                            {doc.description && (
                              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginLeft: '22px', marginTop: '2px' }}>
                                {doc.description}
                              </div>
                            )}
                          </div>
                          {isAdmin && doc.filename && confirmDeleteKnowledge === doc.filename && (
                            <div
                              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Delete this section?</span>
                              <button
                                type="button"
                                style={{ ...btnDanger, padding: '6px 12px', fontSize: '12px' }}
                                disabled={busy === `delete-knowledge-${doc.filename}`}
                                onClick={() => deleteKnowledge(doc)}
                              >
                                {busy === `delete-knowledge-${doc.filename}` ? 'Deleting…' : 'Delete'}
                              </button>
                              <button
                                type="button"
                                style={{ ...btnGhost, padding: '6px 12px', fontSize: '12px' }}
                                onClick={() => setConfirmDeleteKnowledge(null)}
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                          {isAdmin && confirmDeleteKnowledge !== doc.filename && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {!doc.legacy && doc.filename && (
                              <button
                                type="button"
                                title="Delete section"
                                style={{ ...btnGhost, padding: '6px 10px', display: 'flex', alignItems: 'center' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setError('');
                                  setConfirmDeleteKnowledge(doc.filename!);
                                }}
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                            <button
                              type="button"
                              style={{
                                ...btnGhost,
                                padding: '6px 12px',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setError('');
                                setEditingKnowledge({
                                  filename: doc.filename || `${doc.name.toLowerCase().replace(/ /g, '_')}.yaml`,
                                  name: doc.name,
                                  description: doc.description || '',
                                  content: doc.content,
                                  legacy: !!doc.legacy,
                                });
                              }}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '14px', height: '14px' }}>
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                              </svg>
                              Edit
                            </button>
                            </div>
                          )}
                        </div>

                        {/* Expandable Content */}
                        {isExpanded && (
                          <div style={{ marginTop: '12px', borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
                            <pre style={{
                              margin: 0,
                              whiteSpace: 'pre-wrap',
                              fontSize: '12px',
                              color: 'var(--text-muted)',
                              fontFamily: 'ui-monospace, Monaco, monospace',
                              maxHeight: '400px',
                              overflow: 'auto',
                              background: 'var(--surface)',
                              padding: '12px',
                              borderRadius: '6px',
                              lineHeight: 1.5
                            }}>
                              {doc.content}
                            </pre>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Agent Context Section */}
              {knowledgeSection === 'agents' && (
                <div>
                  <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '12px' }}>
                    Dataset-specific context and instructions for each agent. Open one to browse it by section; edit it in the Data Agents tab.
                  </p>
                  
                  {filteredKnowledgeAgents.length === 0 && (
                    <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>No agent context found.</div>
                  )}
                  
                  {filteredKnowledgeAgents.map((a) => {
                    const isExpanded = expandedKnowledge.has(a.task_name);
                    const contextLineCount = a.context ? a.context.split('\n').length : 0;
                    
                    return (
                      <div key={a.task_name} style={{ 
                        ...cardStyle,
                        overflow: 'hidden',
                        transition: 'all 0.2s'
                      }}>
                        {/* Header - Always Visible */}
                        <div 
                          style={{ 
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}
                          onClick={() => {
                            if (agents.some((full) => full.can_view_details && full.task_name === a.task_name)) {
                              openAgent(a.task_name, { tab: 'knowledge' });
                              return;
                            }
                            const newExpanded = new Set(expandedKnowledge);
                            if (isExpanded) {
                              newExpanded.delete(a.task_name);
                            } else {
                              newExpanded.add(a.task_name);
                            }
                            setExpandedKnowledge(newExpanded);
                          }}
                        >
                          <div style={{ 
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '4px'
                          }}>
                            <svg 
                              viewBox="0 0 24 24" 
                              fill="none" 
                              stroke="currentColor" 
                              strokeWidth="2" 
                              style={{ 
                                width: '14px', 
                                height: '14px',
                                color: 'var(--text-muted)',
                                transition: 'transform 0.2s',
                                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                              }}
                            >
                              <path d="M9 18l6-6-6-6"/>
                            </svg>
                            {a.display_name}
                          </div>
                          <div style={{ 
                            fontSize: '11px', 
                            color: 'var(--text-muted)',
                            marginLeft: '20px'
                          }}>
                            {(a.tables || []).length} tables • {(a.questions || []).length} questions • {contextLineCount} lines of context
                          </div>
                        </div>

                        {/* Expandable Content */}
                        {isExpanded && a.context && (
                          <div style={{ marginTop: '12px', borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
                            <pre style={{
                              margin: 0,
                              whiteSpace: 'pre-wrap',
                              fontSize: '12px',
                              color: 'var(--text-muted)',
                              fontFamily: 'ui-monospace, Monaco, monospace',
                              maxHeight: '400px',
                              overflow: 'auto',
                              background: 'var(--surface)',
                              padding: '12px',
                              borderRadius: '6px',
                              lineHeight: 1.5
                            }}>
                              {a.context}
                            </pre>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {!loading && tab === 'schedules' && (
            <div>
              <h3 style={panelTitle}>Schedules</h3>
              <p style={panelSub}>
                Daily digest runs per Data Agent. "Run now" triggers immediately — Slack lands in a few minutes.
              </p>
              
              {/* Debug: show what we're filtering */}
              {isAdmin && (
                <div style={{ 
                  fontSize: '11px', 
                  color: 'var(--text-muted)', 
                  marginBottom: '12px',
                  fontFamily: 'ui-monospace, monospace'
                }}>
                  Showing {agents.filter((a) => a.managed_by_yaml && a.digest_enabled && a.schedule).length} of {agents.filter((a) => a.managed_by_yaml).length} agents
                  {' • '}
                  digest_enabled: {agents.filter((a) => a.managed_by_yaml && a.digest_enabled).length}
                  {' • '}
                  with_schedule: {agents.filter((a) => a.managed_by_yaml && a.schedule).length}
                </div>
              )}
              
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ color: 'var(--text-muted)', textAlign: 'left' }}>
                    <th style={{ padding: '8px 6px', borderBottom: '1px solid var(--border)', fontWeight: 600 }}>Agent</th>
                    <th style={{ padding: '8px 6px', borderBottom: '1px solid var(--border)', fontWeight: 600, width: '180px' }}>Schedule (UTC)</th>
                    <th style={{ padding: '8px 6px', borderBottom: '1px solid var(--border)', fontWeight: 600, width: '200px' }}>Slack</th>
                    <th style={{ padding: '8px 6px', borderBottom: '1px solid var(--border)', width: '100px' }} />
                  </tr>
                </thead>
                <tbody>
                  {agents.filter((a) => a.managed_by_yaml && a.digest_enabled && a.schedule).map((a) => (
                    <tr key={a.task_name}>
                      <td style={{ padding: '10px 6px', borderBottom: '1px solid var(--border)' }}>{a.display_name}</td>
                      <td style={{ padding: '10px 6px', borderBottom: '1px solid var(--border)', fontFamily: 'ui-monospace, monospace', fontSize: '12px', width: '180px' }}>
                        {a.schedule || '—'}
                      </td>
                      <td style={{ padding: '10px 6px', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)', width: '200px' }}>
                        {a.slack_channel_id 
                          ? `#${channels.find(ch => ch.id === a.slack_channel_id)?.name || a.slack_channel_id}` 
                          : a.slack_channel 
                            ? `#${a.slack_channel.replace(/^#/, '')}` 
                            : 'default'}
                      </td>
                      <td style={{ padding: '10px 6px', borderBottom: '1px solid var(--border)', textAlign: 'right', width: '100px' }}>
                        {isAdmin && (
                          <button
                            type="button"
                            style={btnGhost}
                            disabled={busy === `run:${a.task_name}`}
                            onClick={() => runDigest(a.task_name)}
                          >
                            {busy === `run:${a.task_name}` ? 'Running…' : 'Run now'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!agents.some((a) => a.managed_by_yaml) && (
                <div style={{ color: 'var(--text-muted)', fontSize: '13px', marginTop: '12px' }}>No scheduled Data Agents.</div>
              )}
            </div>
          )}

          {!loading && tab === 'analytics' && isAdmin && (
            <div>
              <h3 style={panelTitle}>Analytics Dashboard</h3>
              <p style={panelSub}>
                Usage metrics, user activity, and configuration audit trail.
              </p>
              <AnalyticsDashboard compact />
            </div>
          )}

          {!loading && tab === 'users' && (
            <div>
              <h3 style={panelTitle}>Users</h3>
              <p style={panelSub}>People with access to Pearson Analyst. Deactivated users are shown in gray.</p>
              {isAdmin && (
                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                  <input
                    style={{ ...inputStyle, flex: 1, minWidth: '200px' }}
                    type="email"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    placeholder="name@forbesadvisor.com"
                    onKeyDown={(e) => { if (e.key === 'Enter') addUser(); }}
                  />
                  <select
                    value={newUserRole}
                    onChange={(e) => setNewUserRole(e.target.value)}
                    style={{ ...inputStyle, width: 'auto' }}
                  >
                    <option value="viewer">Viewer</option>
                    <option value="agent_manager">Agent Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                  <button type="button" style={btnPrimary} disabled={busy === 'user-add'} onClick={addUser}>
                    Add
                  </button>
                </div>
              )}
              {[...users]
                .sort((a, b) => {
                  // Sort by active status first (active users on top)
                  if (a.is_active !== false && b.is_active === false) return -1;
                  if (a.is_active === false && b.is_active !== false) return 1;
                  // Then by role priority: admin > agent_manager > viewer
                  const rolePriority = { admin: 3, agent_manager: 2, viewer: 1 };
                  const aPriority = rolePriority[a.role as keyof typeof rolePriority] || 0;
                  const bPriority = rolePriority[b.role as keyof typeof rolePriority] || 0;
                  if (aPriority !== bPriority) return bPriority - aPriority;
                  // Then sort alphabetically by name or email
                  const aName = a.first_name && a.last_name ? `${a.first_name} ${a.last_name}` : a.email;
                  const bName = b.first_name && b.last_name ? `${b.first_name} ${b.last_name}` : b.email;
                  return aName.localeCompare(bName);
                })
                .map((u) => {
                  const isInactive = u.is_active === false;
                  return (
                    <div key={u.email} style={{ 
                      ...cardStyle, 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      gap: '12px',
                      opacity: isInactive ? 0.6 : 1,
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ 
                          fontWeight: 600, 
                          fontSize: '13px',
                          color: isInactive ? 'var(--text-muted)' : 'var(--text)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <span>{u.first_name && u.last_name ? `${u.first_name} ${u.last_name}` : u.email}</span>
                          {isNewUser(u.created_at) && (
                            <span style={{
                              fontSize: '10px',
                              fontWeight: 600,
                              padding: '2px 6px',
                              background: 'var(--accent)',
                              color: '#fff',
                              borderRadius: '4px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              New
                            </span>
                          )}
                          {isInactive && (
                            <span style={{ 
                              fontWeight: 400,
                              marginLeft: '0'
                            }}>
                              (Deactivated)
                            </span>
                          )}
                        </div>
                        {(u.first_name || u.last_name) && (
                          <div style={{ 
                            fontSize: '12px', 
                            color: 'var(--text-muted)', 
                            marginTop: '2px' 
                          }}>
                            {u.email}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                        {isAdmin && !isInactive ? (
                          <select
                            value={u.role}
                            onChange={(e) => setUserRole(u.email, e.target.value, u.role)}
                            style={{ ...inputStyle, width: 'auto', padding: '6px 10px' }}
                            disabled={busy.startsWith('role:')}
                          >
                            <option value="viewer">Viewer</option>
                            <option value="agent_manager">Agent Manager</option>
                            <option value="admin">Admin</option>
                          </select>
                        ) : (
                          <Chip>{u.role.replace('_', ' ')}</Chip>
                        )}
                        {isAdmin && !isInactive && (
                          <button 
                            type="button" 
                            style={btnDanger} 
                            disabled={busy === `rm:${u.email}`}
                            onClick={() => removeUser(u.email)}
                          >
                            {busy === `rm:${u.email}` ? 'Removing...' : 'Remove'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              {!users.length && (
                <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>No users found.</div>
              )}
            </div>
          )}

          {!loading && tab === 'groups' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
                <div>
                  <h3 style={panelTitle}>Permission Groups</h3>
                  <p style={panelSub}>
                    {groupsView === 'groups'
                      ? 'Groups used in Data Agent access lists.'
                      : isAdmin
                        ? 'Each person, their role, their groups and the Data Agents they can open.'
                        : 'Members of the groups you manage and the Data Agents those groups give them.'}
                  </p>
                </div>
                <div role="group" aria-label="View" style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden', flexShrink: 0 }}>
                  {([
                    ['groups', Users, 'By group'],
                    ['users', User, 'By user'],
                  ] as const).map(([view, Icon, label]) => (
                    <button
                      key={view}
                      type="button"
                      title={label}
                      aria-label={label}
                      aria-pressed={groupsView === view}
                      onClick={() => setGroupsView(view)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '7px 10px',
                        border: 'none',
                        cursor: 'pointer',
                        background: groupsView === view ? 'color-mix(in srgb, var(--accent) 16%, transparent)' : 'transparent',
                        color: groupsView === view ? 'var(--accent)' : 'var(--text-muted)',
                      }}
                    >
                      <Icon size={16} aria-hidden />
                    </button>
                  ))}
                </div>
              </div>

              {groupsView === 'users' && (
                <div>
                  <input
                    type="search"
                    value={accessQuery}
                    onChange={(e) => setAccessQuery(e.target.value)}
                    placeholder="Search people, groups or agents"
                    style={{ ...inputStyle, marginBottom: '14px' }}
                  />
                  {accessError && <div style={{ color: 'var(--red)', fontSize: '13px', marginBottom: '12px' }}>{accessError}</div>}
                  {!access && !accessError && (
                    <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Loading access…</div>
                  )}
                  {access &&
                    (() => {
                      const q = accessQuery.trim().toLowerCase();
                      const shown = access.filter(
                        (u) =>
                          !q ||
                          [u.email, u.first_name, u.last_name, ...u.groups.map((g) => g.name), ...u.agents.map((a) => a.display_name)]
                            .filter(Boolean)
                            .some((s) => String(s).toLowerCase().includes(q))
                      );
                      if (!shown.length) {
                        return <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>No matching people.</div>;
                      }
                      const th: React.CSSProperties = {
                        textAlign: 'left',
                        padding: '8px 12px',
                        fontSize: '11px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        color: 'var(--text-muted)',
                        borderBottom: '1px solid var(--border)',
                      };
                      const td: React.CSSProperties = { padding: '10px 12px', verticalAlign: 'middle', borderBottom: '1px solid var(--border)', fontSize: '12px' };
                      const userName = (u: UserAccess) => (u.first_name && u.last_name ? `${u.first_name} ${u.last_name}` : u.email.split('@')[0]).toLowerCase();
                      const sortValue = (u: UserAccess): string | number =>
                        accessSort.key === 'user'
                          ? userName(u)
                          : accessSort.key === 'groups'
                            ? u.groups.length
                            : u.all_agents
                              ? Number.MAX_SAFE_INTEGER
                              : u.agents.length;
                      const sorted = [...shown].sort((a, b) => {
                        const x = sortValue(a);
                        const y = sortValue(b);
                        const c = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
                        return (accessSort.dir === 'asc' ? c : -c) || userName(a).localeCompare(userName(b));
                      });
                      const sortHeader = (key: 'user' | 'groups' | 'access', label: string, align: 'left' | 'right' = 'left') => {
                        const active = accessSort.key === key;
                        const Icon = !active ? ChevronsUpDown : accessSort.dir === 'asc' ? ArrowUp : ArrowDown;
                        return (
                          <button
                            type="button"
                            onClick={() => setAccessSort(active ? { key, dir: accessSort.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'user' ? 'asc' : 'desc' })}
                            aria-label={`Sort by ${label}`}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: 0,
                              border: 'none',
                              background: 'none',
                              font: 'inherit',
                              textTransform: 'inherit',
                              letterSpacing: 'inherit',
                              color: active ? 'var(--text)' : 'inherit',
                              cursor: 'pointer',
                              flexDirection: align === 'right' ? 'row-reverse' : 'row',
                            }}
                          >
                            {label}
                            <Icon size={12} aria-hidden style={{ opacity: active ? 1 : 0.5 }} />
                          </button>
                        );
                      };
                      return (
                        <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: '10px', background: 'var(--surface2)' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '560px' }}>
                            <thead>
                              <tr>
                                <th style={th} aria-sort={accessSort.key === 'user' ? (accessSort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                                  {sortHeader('user', 'User')}
                                </th>
                                <th style={{ ...th, width: '32%' }} aria-sort={accessSort.key === 'groups' ? (accessSort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                                  {sortHeader('groups', 'Groups')}
                                </th>
                                <th style={{ ...th, width: '140px', textAlign: 'right' }} aria-sort={accessSort.key === 'access' ? (accessSort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                                  {sortHeader('access', 'Access', 'right')}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {sorted.map((u) => {
                                const inactive = u.is_active === false;
                                return (
                                  <tr
                                    key={u.email}
                                    onClick={() => setAccessUser(u.email)}
                                    style={{ cursor: 'pointer', opacity: inactive ? 0.6 : 1, background: accessUser === u.email ? 'var(--hover-bg)' : undefined }}
                                  >
                                    <td style={td}>
                                      <AccessUserName u={u} />
                                    </td>
                                    <td style={{ ...td, color: u.groups.length ? 'var(--text)' : 'var(--text-muted)' }}>
                                      {u.groups.length ? u.groups.map((g) => g.name).join(', ') : 'No groups'}
                                    </td>
                                    <td style={{ ...td, textAlign: 'right' }}>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setAccessUser(u.email);
                                        }}
                                        style={{ ...btnGhost, padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                      >
                                        {u.all_agents ? 'All agents' : u.agents.length ? `${u.agents.length} agent${u.agents.length !== 1 ? 's' : ''}` : 'No agents'}
                                        <ChevronRight size={14} aria-hidden />
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}
                  {(() => {
                    const u = access?.find((x) => x.email === accessUser);
                    if (!u) return null;
                    const byGroup = new Map<string, UserAccess['agents']>();
                    const direct: UserAccess['agents'] = [];
                    const everyone: UserAccess['agents'] = [];
                    for (const a of u.agents) {
                      for (const v of a.via) {
                        if (v.startsWith('group:')) byGroup.set(v.slice(6), [...(byGroup.get(v.slice(6)) || []), a]);
                        else if (v === 'direct') direct.push(a);
                        else if (v === 'everyone') everyone.push(a);
                      }
                    }
                    // One look for every section: a header row, then a bordered list with divided rows.
                    const listBox: React.CSSProperties = { border: '1px solid var(--border)', borderRadius: '10px', background: 'var(--surface2)', overflow: 'hidden' };
                    const rowStyle = (first: boolean): React.CSSProperties => ({
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px 12px',
                      borderTop: first ? 'none' : '1px solid var(--border)',
                    });
                    const primary: React.CSSProperties = { fontSize: '13px', fontWeight: 600, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '6px' };
                    const secondary: React.CSSProperties = { fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px', lineHeight: 1.45 };
                    const rowBtn: React.CSSProperties = { ...btnGhost, padding: '4px 10px', fontSize: '12px', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '4px' };
                    const sectionHead = (title: string, hint?: string, action?: React.ReactNode) => (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '12px', margin: '24px 0 8px' }}>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: 700 }}>{title}</div>
                          {hint && <div style={secondary}>{hint}</div>}
                        </div>
                        {action}
                      </div>
                    );
                    const agentRows = (list: UserAccess['agents'], editable: boolean) => (
                      <div style={listBox}>
                        {list.map((a, i) => {
                          const full = agents.find((x) => x.task_name === a.task_name);
                          return (
                            <div key={a.task_name} style={rowStyle(i === 0)}>
                              <div style={{ flex: 1, minWidth: 0, ...primary, fontWeight: 500 }}>{a.display_name}</div>
                              {editable && full && canManageAgents && (
                                <button type="button" style={rowBtn} onClick={() => navigate('/settings/editagent', { state: { editAgent: full } })}>
                                  Edit agent
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                    const candidates = groups.filter((g) => !u.groups.some((x) => x.name === g.name));
                    const submitGroups = async () => {
                      if (!addGroups?.size) return;
                      setAddGroupsBusy(true);
                      setAddGroupsError('');
                      try {
                        await api.post(`/api/users/${encodeURIComponent(u.email)}/groups`, { groups: [...addGroups] });
                        setAddGroups(null);
                        await loadGroups();
                        const fresh = await api.get('/api/users/access'); // refresh in place so the panel stays open
                        setAccess(fresh.data.users || []);
                        setNotice(`Added ${u.email} to ${addGroups.size} group${addGroups.size !== 1 ? 's' : ''}`);
                        setTimeout(() => setNotice(''), 3000);
                      } catch (err: any) {
                        setAddGroupsError(extractErrorMessage(err, 'Could not add to groups. Please try again.'));
                      } finally {
                        setAddGroupsBusy(false);
                      }
                    };
                    const accessSummary = u.all_agents
                      ? 'Admins can open every Data Agent.'
                      : u.agents.length
                        ? `Can open ${u.agents.length} Data Agent${u.agents.length !== 1 ? 's' : ''}.`
                        : isAdmin
                          ? 'No access to any Data Agent yet.'
                          : 'Your groups don’t give this person any Data Agents.';
                    return (
                      <div
                        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1500 }}
                        onClick={(e) => {
                          if (e.target === e.currentTarget) setAccessUser(null);
                        }}
                      >
                        <aside
                          role="dialog"
                          aria-modal="true"
                          aria-label={`Access for ${u.email}`}
                          style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            bottom: 0,
                            width: '440px',
                            maxWidth: '100vw',
                            background: 'var(--surface)',
                            borderLeft: '1px solid var(--border)',
                            padding: 'calc(20px + env(safe-area-inset-top, 0px)) 24px 28px',
                            overflowY: 'auto',
                            boxShadow: '-8px 0 24px rgba(0,0,0,0.25)',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', paddingBottom: '16px', borderBottom: '1px solid var(--border)' }}>
                            <div style={{ minWidth: 0 }}>
                              <AccessUserName u={u} />
                              <div style={{ ...secondary, marginTop: '8px' }}>{accessSummary}</div>
                            </div>
                            <button type="button" aria-label="Close" onClick={() => setAccessUser(null)} style={{ ...btnGhost, padding: '6px', display: 'inline-flex' }}>
                              <X size={14} aria-hidden />
                            </button>
                          </div>

                          {sectionHead(
                            'Groups',
                            'Agents they get through each group.',
                            candidates.length > 0 && !addGroups ? (
                              <button type="button" style={rowBtn} onClick={() => setAddGroups(new Set())}>
                                <Plus size={12} aria-hidden /> Add to groups
                              </button>
                            ) : undefined
                          )}
                          {addGroups && (
                            <div style={{ ...listBox, marginBottom: '10px', borderColor: 'var(--accent)' }}>
                              <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
                                {candidates.map((g, i) => (
                                  <label key={g.name} style={{ ...rowStyle(i === 0), alignItems: 'flex-start', cursor: 'pointer' }}>
                                    <input
                                      type="checkbox"
                                      style={{ marginTop: '2px', accentColor: 'var(--accent)' }}
                                      checked={addGroups.has(g.name)}
                                      onChange={(e) => {
                                        const next = new Set(addGroups);
                                        if (e.target.checked) next.add(g.name);
                                        else next.delete(g.name);
                                        setAddGroups(next);
                                      }}
                                    />
                                    <div style={{ minWidth: 0 }}>
                                      <div style={primary}>{g.name}</div>
                                      {g.description && <div style={secondary}>{g.description}</div>}
                                    </div>
                                  </label>
                                ))}
                              </div>
                              <div style={{ ...rowStyle(false), justifyContent: 'flex-end', background: 'var(--surface)' }}>
                                {addGroupsError && <div style={{ flex: 1, color: 'var(--red)', fontSize: '12px' }}>{addGroupsError}</div>}
                                <button type="button" style={rowBtn} disabled={addGroupsBusy} onClick={() => setAddGroups(null)}>
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  style={{ ...btnPrimary, padding: '5px 12px', fontSize: '12px', opacity: !addGroups.size || addGroupsBusy ? 0.6 : 1 }}
                                  disabled={!addGroups.size || addGroupsBusy}
                                  onClick={submitGroups}
                                >
                                  {addGroupsBusy ? 'Adding…' : addGroups.size ? `Add to ${addGroups.size} group${addGroups.size !== 1 ? 's' : ''}` : 'Add'}
                                </button>
                              </div>
                            </div>
                          )}
                          {u.groups.length ? (
                            <div style={listBox}>
                              {u.groups.map((g, i) => {
                                const full = groups.find((x) => x.name === g.name);
                                const list = byGroup.get(g.name) || [];
                                return (
                                  <div key={g.name} style={{ ...rowStyle(i === 0), alignItems: 'flex-start' }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={primary}>
                                        {g.name}
                                        {g.is_admin && (
                                          <span title="Group admin" style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '11px', fontWeight: 600, color: 'var(--accent)' }}>
                                            <ShieldCheck size={12} aria-hidden /> admin
                                          </span>
                                        )}
                                      </div>
                                      <div style={secondary}>{list.length ? list.map((a) => a.display_name).join(' · ') : 'No Data Agents use this group'}</div>
                                    </div>
                                    {full && (
                                      <button
                                        type="button"
                                        style={rowBtn}
                                        onClick={() => {
                                          setAccessUser(null);
                                          openGroupEditor(full);
                                        }}
                                      >
                                        Edit group
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            !addGroups && <div style={{ ...listBox, ...secondary, marginTop: 0, padding: '12px' }}>Not in any group.</div>
                          )}

                          {direct.length > 0 && (
                            <>
                              {sectionHead('Added directly', 'Their email is on the agent’s own access list.')}
                              {agentRows(direct, true)}
                            </>
                          )}

                          {everyone.length > 0 && (
                            <>
                              {sectionHead('Open to everyone', 'Anyone who can sign in can open these.')}
                              {agentRows(everyone, false)}
                            </>
                          )}
                        </aside>
                      </div>
                    );
                  })()}
                </div>
              )}

              {groupsView === 'groups' && isAdmin && (
                <button 
                  type="button" 
                  style={{ ...btnPrimary, marginBottom: '18px' }}
                  onClick={() => openGroupEditor(null)}
                >
                  + New Group
                </button>
              )}

              {groupsView === 'groups' && groups.map((g) => {
                const memberPreview = g.members.slice(0, 3).map((email) => {
                  const u = users.find((user) => user.email === email);
                  const isInactive = u && u.is_active === false;
                  const displayName = u && u.first_name && u.last_name 
                    ? `${u.first_name} ${u.last_name}` 
                    : email.split('@')[0];
                  return isInactive ? `${displayName} (Deactivated)` : displayName;
                }).join(', ');
                const moreCount = g.members.length > 3 ? ` +${g.members.length - 3}` : '';
                
                const adminPreview = g.admins.length > 0 
                  ? g.admins.map((email) => {
                      const u = users.find((user) => user.email === email);
                      const isInactive = u && u.is_active === false;
                      const displayName = u && u.first_name && u.last_name 
                        ? `${u.first_name} ${u.last_name}` 
                        : email.split('@')[0];
                      return isInactive ? `${displayName} (Deactivated)` : displayName;
                    }).join(', ')
                  : '';

                return (
                  <div key={g.name} style={{
                    ...cardStyle,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '2px' }}>
                        {g.name}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                        {g.members.length > 0 ? (
                          <>
                            {memberPreview}{moreCount}
                            <span style={{ marginLeft: '6px', color: 'var(--border)' }}>·</span>
                            <span style={{ marginLeft: '6px' }}>
                              {g.members.length} member{g.members.length !== 1 ? 's' : ''}
                            </span>
                          </>
                        ) : (
                          <span>No members</span>
                        )}
                        {adminPreview && (
                          <span style={{ marginLeft: '10px', color: 'var(--accent)' }} title="Group admins">
                            ⚙ {adminPreview}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      style={{ ...btnGhost, flexShrink: 0 }}
                      onClick={() => openGroupEditor(g)}
                    >
                      Edit
                    </button>
                  </div>
                );
              })}
              {groupsView === 'groups' && !groups.length && (
                <div style={{ 
                  padding: '24px', 
                  textAlign: 'center', 
                  color: 'var(--text-muted)', 
                  fontSize: '13px',
                  background: 'var(--surface2)',
                  borderRadius: '10px',
                  border: '1px solid var(--border)'
                }}>
                  No groups found.
                </div>
              )}
            </div>
          )}

          {!loading && tab === 'slack' && isAdmin && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div>
                  <h3 style={panelTitle}>Slack Tools</h3>
                  <p style={panelSub}>Allowed channels for digests and bot posts. Add by Slack channel ID.</p>
                </div>
                {channels.length > 0 && (
                  <button
                    type="button"
                    style={{
                      ...btnGhost,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 12px',
                      fontSize: '13px'
                    }}
                    disabled={busy === 'slack-refresh'}
                    onClick={refreshAllChannels}
                    title="Refresh all channel names from Slack"
                  >
                    <RefreshCw size={14} style={{ opacity: 0.7 }} />
                    Refresh All
                  </button>
                )}
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '14px' }}>
                <thead>
                  <tr style={{ color: 'var(--text-muted)', textAlign: 'left' }}>
                    <th style={{ padding: '8px 6px', borderBottom: '1px solid var(--border)' }}>Channel ID</th>
                    <th style={{ padding: '8px 6px', borderBottom: '1px solid var(--border)' }}>Name</th>
                    <th style={{ padding: '8px 6px', borderBottom: '1px solid var(--border)' }}>Label</th>
                    <th style={{ padding: '8px 6px', borderBottom: '1px solid var(--border)', width: '120px' }}>Status</th>
                    <th style={{ padding: '8px 6px', borderBottom: '1px solid var(--border)', width: '100px' }} />
                  </tr>
                </thead>
                <tbody>
                  {channels.map((ch) => {
                    const isEnabled = ch.enabled !== false; // Default to true if undefined
                    const isProtected = ch.id === FAIL_CHANNEL;
                    return (
                      <tr key={ch.id}>
                        <td style={{ padding: '10px 6px', borderBottom: '1px solid var(--border)', fontFamily: 'ui-monospace, monospace', fontSize: '12px' }}>{ch.id}</td>
                        <td style={{ padding: '10px 6px', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)' }}>
                          {ch.name ? `#${ch.name}` : '(unresolved)'}
                        </td>
                        <td style={{ padding: '10px 6px', borderBottom: '1px solid var(--border)' }}>{ch.label || ''}</td>
                        <td style={{ padding: '10px 6px', borderBottom: '1px solid var(--border)' }}>
                          {isProtected ? (
                            <span style={{ 
                              display: 'inline-flex', 
                              alignItems: 'center', 
                              gap: '6px', 
                              padding: '6px 12px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: 500,
                              backgroundColor: 'rgba(34, 197, 94, 0.1)',
                              color: '#22c55e',
                              border: '1px solid rgba(34, 197, 94, 0.3)',
                              minWidth: '90px',
                              justifyContent: 'center'
                            }}>
                              <CheckCircle size={14} />
                              Active
                            </span>
                          ) : (
                            <button
                              type="button"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '6px 12px',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: 500,
                                minWidth: '90px',
                                justifyContent: 'center',
                                border: isEnabled ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid rgba(239, 68, 68, 0.3)',
                                backgroundColor: isEnabled ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                color: isEnabled ? '#22c55e' : '#ef4444',
                                cursor: 'pointer',
                                opacity: busy === `slack-toggle:${ch.id}` ? 0.5 : 1,
                                transition: 'all 0.15s ease'
                              }}
                              disabled={busy === `slack-toggle:${ch.id}`}
                              onClick={() => toggleSlackChannel(ch.id, isEnabled)}
                              title={isEnabled ? 'Click to disable (maintenance mode)' : 'Click to enable'}
                              onMouseEnter={(e) => {
                                if (busy !== `slack-toggle:${ch.id}`) {
                                  e.currentTarget.style.opacity = '0.8';
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.opacity = busy === `slack-toggle:${ch.id}` ? '0.5' : '1';
                              }}
                            >
                              {isEnabled ? (
                                <>
                                  <CheckCircle size={14} />
                                  Active
                                </>
                              ) : (
                                <>
                                  <Ban size={14} />
                                  Disabled
                                </>
                              )}
                            </button>
                          )}
                        </td>
                        <td style={{ padding: '10px 6px', borderBottom: '1px solid var(--border)', textAlign: 'right' }}>
                          {isProtected ? (
                            <Chip>protected</Chip>
                          ) : (
                            <button type="button" style={btnDanger} onClick={() => removeSlackChannel(ch.id)}>
                              Remove
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!channels.length && (
                <div style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '12px' }}>No channels yet.</div>
              )}

              <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
                <input
                  style={inputStyle}
                  value={slackChannelId}
                  onChange={(e) => setSlackChannelId(e.target.value)}
                  placeholder="C0…"
                />
                <button type="button" style={btnPrimary} disabled={busy === 'slack-add'} onClick={addSlackChannel}>
                  Add channel
                </button>
              </div>

              <div style={{
                marginTop: '24px',
                padding: '16px',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                backgroundColor: 'rgba(239, 68, 68, 0.05)'
              }}>
                <h4 style={{ margin: '0 0 8px', fontSize: '14px' }}>Delete a message</h4>
                <p style={{ ...panelSub, marginBottom: '12px' }}>
                  Paste a Slack message link posted by the agent.
                </p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    style={inputStyle}
                    value={slackMsgUrl}
                    onChange={(e) => setSlackMsgUrl(e.target.value)}
                    placeholder="https://…slack.com/archives/C…/p…"
                  />
                  <button type="button" style={btnDanger} disabled={busy === 'slack-del-msg'} onClick={deleteSlackMessage}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}

          {!loading && tab === 'account' && (
            <div>
              <h3 style={panelTitle}>Account</h3>
              <div style={cardStyle}>
                <Row label="Signed in as" value={role?.email || user?.email || '—'} />
                <Row label="Role" value={(role?.role || user?.role || 'viewer').replace('_', ' ').toUpperCase()} />
                <Row label="Can manage Data Agents" value={canManageAgents ? 'Yes' : 'No — ask an admin'} />
              </div>

              <h3 style={{ ...panelTitle, marginTop: '24px' }}>Data retention</h3>
              <p style={panelSub}>Your data is deleted automatically after these periods.</p>
              {privacyError && (
                <div style={{ ...cardStyle, color: 'var(--red)', fontSize: '13px' }}>{privacyError}</div>
              )}
              {privacy && (
                <>
                  <div style={cardStyle}>
                    {privacy.retention.map((r) => (
                      <div
                        key={r.key}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: '16px',
                          padding: '10px 0',
                          borderBottom: '1px solid var(--border)',
                          fontSize: '13px',
                        }}
                      >
                        <div>
                          <div style={{ color: 'var(--text-muted)' }}>{r.label}</div>
                          <div style={{ fontSize: '12px', color: 'var(--text-muted)', opacity: 0.75, marginTop: '2px' }}>{r.detail}</div>
                        </div>
                        <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{r.days} days</span>
                      </div>
                    ))}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '8px',
                      paddingTop: '10px',
                      fontSize: '12px',
                      color: 'var(--text-muted)',
                    }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <Lock size={11} /> Fixed periods, can't be changed
                      </span>
                      {privacy.privacy_policy_url ? (
                        <a
                          href={privacy.privacy_policy_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: 'var(--accent)', display: 'inline-flex', alignItems: 'center', gap: '5px', textDecoration: 'none' }}
                        >
                          <ShieldCheck size={12} /> {privacy.privacy_policy_label} <ExternalLink size={11} />
                        </a>
                      ) : isAdmin && (
                        <span title="Set PRIVACY_POLICY_URL on the server to show this link to everyone. Only admins see this.">
                          Policy link not configured
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{
                    ...cardStyle,
                    marginTop: '16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '12px',
                  }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 600 }}>Delete my history</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                        Permanently delete your chats, feedback and Claude / Copilot activity.
                      </div>
                    </div>
                    <button
                      type="button"
                      style={{ ...btnDanger, display: 'inline-flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}
                      onClick={() => { setDeleteResult(''); setDeleteTarget({ email: null }); }}
                    >
                      <Trash2 size={13} /> Delete
                    </button>
                    {deleteResult && (
                      <div style={{ flexBasis: '100%', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <CheckCircle size={13} color="var(--green, #22c55e)" /> {deleteResult}
                      </div>
                    )}
                  </div>
                </>
              )}

              <div>
                <button
                  type="button"
                  style={{ ...btnGhost, marginTop: '24px', color: 'var(--red)' }}
                  onClick={() => { window.location.href = '/auth/logout'; }}
                >
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Group Editor Modal */}
      {showGroupModal && editingGroup && (
        <GroupEditorModal
          group={editingGroup}
          users={users}
          isAdmin={isAdmin}
          onSave={saveGroup}
          onDelete={deleteGroup}
          onCancel={() => {
            setShowGroupModal(false);
            setEditingGroup(null);
            setError('');
          }}
          busy={busy}
          error={error}
        />
      )}

      {viewingAgent && (
        <AgentDetailsDrawer
          agent={viewingAgent}
          canManage={canManageAgents}
          initialTab={drawerTab}
          confirmDeleteOnOpen={confirmDeleteFor === viewingAgent.task_name}
          onClose={closeAgent}
          onStartChat={() => navigate(`/?agent=${encodeURIComponent(viewingAgent.task_name)}`)}
          onEdit={() => navigate('/settings/editagent', { state: { editAgent: viewingAgent } })}
          onDuplicate={() => duplicateAgent(viewingAgent)}
          onDelete={() => deleteAgent(viewingAgent)}
        />
      )}

      {deleteTarget && (
        <DeleteHistoryModal
          email={deleteTarget.email}
          onCancel={() => setDeleteTarget(null)}
          onDeleted={(message) => {
            setDeleteTarget(null);
            if (deleteTarget.email) setNotice(message);
            else setDeleteResult(message);
          }}
        />
      )}

      {/* Knowledge Editor Modal */}
      {editingKnowledge && (
        <KnowledgeEditorModal
          knowledge={editingKnowledge}
          onSave={saveKnowledge}
          onCancel={() => {
            setEditingKnowledge(null);
            setError('');
          }}
          busy={busy}
          error={error}
        />
      )}
    </div>
  );
}

interface DeleteResponse {
  chat_deleted: number;
  chat_pending: number;
  mcp_deleted: number;
}

function DeleteHistoryModal({ email, onCancel, onDeleted }: {
  email: string | null;
  onCancel: () => void;
  onDeleted: (message: string) => void;
}) {
  const [confirmed, setConfirmed] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const whose = email ? `${email}'s` : 'your';

  const run = async () => {
    setDeleting(true);
    setError('');
    try {
      const url = email ? `/api/admin/users/${encodeURIComponent(email)}/activity` : '/api/me/history';
      const { data } = await api.delete<DeleteResponse>(url);
      const total = data.chat_deleted + data.mcp_deleted;
      let message = `Deleted ${total} item${total === 1 ? '' : 's'} of ${whose} history.`;
      if (data.chat_pending > 0) {
        message += ` ${data.chat_pending} recent item${data.chat_pending === 1 ? ' is' : 's are'} still being processed. Delete again in about 90 minutes to remove ${data.chat_pending === 1 ? 'it' : 'them'}.`;
      }
      onDeleted(message);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not delete the history. Please try again.');
      setDeleting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !deleting) onCancel(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        style={{
          background: 'var(--surface)',
          borderRadius: '12px',
          padding: '24px',
          width: '460px',
          maxWidth: '95vw',
          border: '1px solid var(--border)'
        }}
      >
        <h3 style={{ margin: '0 0 12px 0', fontSize: '17px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Trash2 size={18} color="var(--red)" /> Permanently delete {whose} history?
        </h3>
        <p style={{ margin: '0 0 12px 0', fontSize: '13px', lineHeight: 1.5 }}>
          This deletes all of {whose} chat history, answer feedback and Claude / Copilot activity
          from Pearson.
        </p>
        <p style={{ margin: '0 0 16px 0', fontSize: '13px', lineHeight: 1.5, color: 'var(--red)', fontWeight: 600 }}>
          This is permanent and can't be undone.
        </p>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', marginBottom: '16px', cursor: 'pointer' }}>
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} disabled={deleting} />
          I understand the deleted data can't be recovered
        </label>
        {error && (
          <div style={{ color: 'var(--red)', fontSize: '13px', marginBottom: '12px' }}>{error}</div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button type="button" style={btnGhost} onClick={onCancel} disabled={deleting}>Cancel</button>
          <button
            type="button"
            style={{ ...btnPrimary, background: 'var(--red)', opacity: confirmed && !deleting ? 1 : 0.5, cursor: confirmed && !deleting ? 'pointer' : 'not-allowed' }}
            disabled={!confirmed || deleting}
            onClick={run}
          >
            {deleting ? 'Deleting…' : 'Delete permanently'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '3px 8px',
      borderRadius: '999px',
      border: '1px solid var(--border)',
      background: 'var(--surface)',
      fontSize: '11px',
      color: 'var(--text-muted)',
    }}>
      {children}
    </span>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: '16px',
      padding: '10px 0',
      borderBottom: '1px solid var(--border)',
      fontSize: '13px',
    }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontWeight: 500, textAlign: 'right' }}>{value}</span>
    </div>
  );
}

interface GroupEditorModalProps {
  group: { name: string; description: string; members: string[]; admins: string[] };
  users: AppUser[];
  isAdmin: boolean;
  onSave: (group: { name: string; description: string; members: string[]; admins: string[] }, isNew: boolean, originalName?: string) => void;
  onDelete: (groupName: string) => void;
  onCancel: () => void;
  busy: string;
  error: string;
}

function GroupEditorModal({ group, users, isAdmin, onSave, onDelete, onCancel, busy, error }: GroupEditorModalProps) {
  const [name, setName] = useState(group.name);
  const [description, setDescription] = useState(group.description);
  const [members, setMembers] = useState<string[]>(group.members);
  const [admins, setAdmins] = useState<string[]>(group.admins);

  const isNew = !group.name;
  const originalName = group.name;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div
        style={{
          background: 'var(--surface)',
          borderRadius: '12px',
          padding: '28px',
          width: '900px',
          maxWidth: '95vw',
          height: '80vh',
          maxHeight: '700px',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid var(--border)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>
            {isNew ? 'New Permission Group' : `Edit Group: ${originalName}`}
          </h3>
        </div>

        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: 'auto', paddingRight: '4px', marginRight: '-4px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>
                Group name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value.toLowerCase().trim())}
                placeholder="e.g. finserv"
                disabled={busy === 'save-group' || busy === 'delete-group'}
                autoComplete="off"
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  background: 'var(--surface2)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  color: 'var(--text)',
                  fontSize: '13px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>
                Description
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Short description"
                disabled={busy === 'save-group' || busy === 'delete-group'}
                autoComplete="off"
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  background: 'var(--surface2)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  color: 'var(--text)',
                  fontSize: '13px',
                  fontFamily: 'inherit'
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600 }}>
              Members <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '12px' }}>({members.length} selected)</span>
            </label>
            <ImprovedChipInput
              values={members}
              onChange={setMembers}
              placeholder="Search by name or email…"
              suggestions={users.map(u => {
                const displayName = u.first_name && u.last_name 
                  ? `${u.first_name} ${u.last_name} (${u.email})`
                  : u.email;
                const isNew = isNewUser(u.created_at);
                const status = u.is_active === false ? ' [Deactivated]' : (isNew ? ' [New User]' : '');
                return {
                  value: u.email,
                  label: displayName + status,
                };
              })}
              disabled={busy === 'save-group' || busy === 'delete-group'}
            />
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '6px' }}>
              Users who can access datasets assigned to this group.
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600 }}>
              Group Admins <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '12px' }}>({admins.length} selected · can edit this group)</span>
            </label>
            <ImprovedChipInput
              values={admins}
              onChange={setAdmins}
              placeholder="Search by name or email…"
              suggestions={users.map(u => {
                const displayName = u.first_name && u.last_name 
                  ? `${u.first_name} ${u.last_name} (${u.email})`
                  : u.email;
                const isNew = isNewUser(u.created_at);
                const status = u.is_active === false ? ' [Deactivated]' : (isNew ? ' [New User]' : '');
                return {
                  value: u.email,
                  label: displayName + status,
                };
              })}
              disabled={busy === 'save-group' || busy === 'delete-group'}
            />
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '6px' }}>
              These users can add/remove members from this group.
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ paddingTop: '20px', borderTop: '1px solid var(--border)', marginTop: '8px' }}>
          {error && (
            <div style={{
              padding: '10px 12px',
              background: '#ff000015',
              border: '1px solid #ff0000',
              borderRadius: '8px',
              color: '#ff0000',
              fontSize: '13px',
              marginBottom: '16px'
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between' }}>
            <div>
              {!isNew && isAdmin && (
                <button
                  type="button"
                  onClick={() => onDelete(originalName)}
                  disabled={busy === 'save-group' || busy === 'delete-group'}
                  style={{
                    padding: '9px 16px',
                    background: 'var(--red)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: busy ? 'not-allowed' : 'pointer',
                    opacity: busy ? 0.5 : 1,
                    fontFamily: 'inherit'
                  }}
                >
                  {busy === 'delete-group' ? 'Deleting…' : 'Delete group'}
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={onCancel}
                disabled={busy === 'save-group' || busy === 'delete-group'}
                style={{
                  padding: '9px 16px',
                  background: 'transparent',
                  color: 'var(--text)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: busy ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => onSave({ name, description, members, admins }, isNew, originalName)}
                disabled={busy === 'save-group' || busy === 'delete-group'}
                style={{
                  padding: '9px 16px',
                  background: 'var(--accent)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: busy ? 'not-allowed' : 'pointer',
                  opacity: busy ? 0.5 : 1,
                  fontFamily: 'inherit'
                }}
              >
                {busy === 'save-group' ? 'Saving…' : (isNew ? 'Create' : 'Save')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ImprovedChipInput({ values, onChange, placeholder, suggestions, disabled }: {
  values: string[];
  onChange: (values: string[]) => void;
  placeholder: string;
  suggestions?: { value: string; label: string }[];
  disabled?: boolean;
}) {
  const [input, setInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

  const filtered = (suggestions || []).filter(s => 
    !values.includes(s.value) && 
    (!input || s.label.toLowerCase().includes(input.toLowerCase()) || s.value.toLowerCase().includes(input.toLowerCase()))
  );

  const addValue = (val: string) => {
    const trimmed = val.trim().toLowerCase();
    if (trimmed && !values.includes(trimmed)) {
      onChange([...values, trimmed]);
    }
    setInput('');
    setSelectedItems(new Set());
    // Keep suggestions open if input is still focused
    if (isFocused) {
      setShowSuggestions(true);
    }
  };

  const addSelectedItems = () => {
    if (selectedItems.size === 0) return;
    const newValues = [...values, ...Array.from(selectedItems)];
    onChange(newValues);
    setSelectedItems(new Set());
    setInput('');
    setShowSuggestions(false);
    setIsFocused(false);
  };

  const toggleSelection = (value: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(value)) {
      newSelected.delete(value);
    } else {
      newSelected.add(value);
    }
    setSelectedItems(newSelected);
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Input field - wrapped in form to control autocomplete */}
      <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '9px 12px',
          background: 'var(--surface2)',
          border: `1px solid ${isFocused ? 'var(--accent)' : 'var(--border)'}`,
          borderRadius: '8px',
          transition: 'border-color 0.15s ease'
        }}>
          <input
            type="text"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              setShowSuggestions(true);
            }}
            onFocus={() => {
              setIsFocused(true);
              setShowSuggestions(true);
            }}
            onBlur={() => {
              setIsFocused(false);
              setTimeout(() => {
                setShowSuggestions(false);
                setSelectedItems(new Set());
              }, 250);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (selectedItems.size > 0) {
                  addSelectedItems();
                } else if (input.trim()) {
                  addValue(input);
                }
              }
            }}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            name="user-search"
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: 'var(--text)',
              fontSize: '13px',
              fontFamily: 'inherit'
            }}
          />
        </div>
      </form>

      {/* Selected values as chips below */}
      {values.length > 0 && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          marginTop: '8px',
          padding: '10px',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: '8px',
          maxHeight: '160px',
          overflowY: 'auto'
        }}>
          {values.map((val, i) => (
            <span
              key={i}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '5px 10px',
                background: 'var(--surface2)',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                fontSize: '12px'
              }}
            >
              {val}
              <button
                type="button"
                onClick={() => onChange(values.filter((_, idx) => idx !== i))}
                disabled={disabled}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  padding: 0,
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Suggestions dropdown */}
      {showSuggestions && isFocused && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 6px)',
          left: 0,
          right: 0,
          maxHeight: '450px',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: '8px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          zIndex: 200,
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Search results header */}
          <div style={{
            padding: '8px 12px',
            borderBottom: '1px solid var(--border)',
            fontSize: '12px',
            color: 'var(--text-muted)',
            fontWeight: 600,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
            {selectedItems.size > 0 && (
              <span style={{ color: 'var(--accent)' }}>
                {selectedItems.size} selected
              </span>
            )}
          </div>

          {/* Scrollable list */}
          <div style={{ overflowY: 'auto', maxHeight: '320px', flex: 1 }}>
            {filtered.length > 0 ? (
              filtered.map((s, i) => {
                const isSelected = selectedItems.has(s.value);
                return (
                  <button
                    key={i}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      toggleSelection(s.value);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      background: isSelected ? 'var(--accent)15' : 'transparent',
                      border: 'none',
                      borderBottom: i < filtered.length - 1 ? '1px solid var(--border)' : 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '13px',
                      color: isSelected ? 'var(--accent)' : 'var(--text)',
                      fontFamily: 'inherit',
                      transition: 'background 0.1s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}
                    onMouseOver={(e) => { 
                      if (!isSelected) {
                        e.currentTarget.style.background = 'var(--surface2)'; 
                      }
                    }}
                    onMouseOut={(e) => { 
                      if (!isSelected) {
                        e.currentTarget.style.background = 'transparent'; 
                      }
                    }}
                  >
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: `2px solid ${isSelected ? 'var(--accent)' : 'var(--border)'}`,
                      borderRadius: '4px',
                      background: isSelected ? 'var(--accent)' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      transition: 'all 0.15s ease'
                    }}>
                      {isSelected && (
                        <span style={{ color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>✓</span>
                      )}
                    </div>
                    <span style={{ flex: 1 }}>{s.label}</span>
                  </button>
                );
              })
            ) : (
              <div style={{
                padding: '20px 14px',
                textAlign: 'center',
                fontSize: '13px',
                color: 'var(--text-muted)'
              }}>
                {input ? 'No users found' : 'Start typing to search users'}
              </div>
            )}
          </div>

          {/* Add selected button */}
          {selectedItems.size > 0 && (
            <div style={{
              padding: '10px 12px',
              borderTop: '1px solid var(--border)',
              display: 'flex',
              gap: '8px'
            }}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setSelectedItems(new Set());
                  setShowSuggestions(false);
                  setIsFocused(false);
                }}
                style={{
                  flex: 1,
                  padding: '8px 14px',
                  background: 'transparent',
                  color: 'var(--text)',
                  border: '1px solid var(--border)',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit'
                }}
              >
                Clear & Close
              </button>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  addSelectedItems();
                }}
                style={{
                  flex: 2,
                  padding: '8px 14px',
                  background: 'var(--accent)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit'
                }}
              >
                Add {selectedItems.size} User{selectedItems.size !== 1 ? 's' : ''}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


interface KnowledgeEditorModalProps {
  knowledge: KnowledgeDraft;
  onSave: (draft: KnowledgeDraft) => void;
  onCancel: () => void;
  busy: string;
  error: string;
}

function KnowledgeEditorModal({ knowledge, onSave, onCancel, busy, error }: KnowledgeEditorModalProps) {
  const [content, setContent] = useState(knowledge.content);
  const [name, setName] = useState(knowledge.name);
  const [description, setDescription] = useState(knowledge.description);
  const isNew = !knowledge.filename;
  const canSave = knowledge.legacy || (name.trim() && description.trim() && content.trim());
  const fieldStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    background: 'var(--surface2)',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    color: 'var(--text)',
    fontSize: '13px',
    fontFamily: 'inherit',
    marginBottom: '12px',
    boxSizing: 'border-box',
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div
        style={{
          background: 'var(--surface)',
          borderRadius: '12px',
          padding: '24px',
          width: '800px',
          maxWidth: '95vw',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid var(--border)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 700 }}>
          {isNew ? 'New business section' : `Edit: ${knowledge.name}`}
        </h3>

        {!knowledge.legacy && (
          <>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={busy === 'save-knowledge'}
              style={fieldStyle}
              placeholder="Section name, e.g. Competitor Rules"
            />
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={busy === 'save-knowledge'}
              style={{ ...fieldStyle, marginBottom: '4px' }}
              placeholder="When should agents use this? e.g. Questions about competitor brands or market share"
            />
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px' }}>
              Agents see only the name and description, and read the full text when a question matches.
            </div>
          </>
        )}

        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          disabled={busy === 'save-knowledge'}
          style={{
            flex: 1,
            width: '100%',
            minHeight: '400px',
            padding: '12px',
            background: 'var(--surface2)',
            border: '1px solid var(--border)',
            borderRadius: '8px',
            color: 'var(--text)',
            fontSize: '13px',
            fontFamily: 'ui-monospace, Monaco, monospace',
            resize: 'vertical',
            marginBottom: '16px'
          }}
          placeholder={isNew ? 'Business rules for this section…' : 'YAML content...'}
        />

        {error && (
          <div style={{
            padding: '10px 12px',
            background: '#ff000015',
            border: '1px solid #ff0000',
            borderRadius: '8px',
            color: '#ff0000',
            fontSize: '13px',
            marginBottom: '16px'
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy === 'save-knowledge'}
            style={{
              padding: '8px 14px',
              background: 'transparent',
              color: 'var(--text)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: 600,
              cursor: busy === 'save-knowledge' ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit'
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave({ ...knowledge, name, description, content })}
            disabled={busy === 'save-knowledge' || !canSave}
            style={{
              padding: '8px 14px',
              background: 'var(--accent)',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: 600,
              cursor: busy === 'save-knowledge' || !canSave ? 'not-allowed' : 'pointer',
              opacity: busy === 'save-knowledge' || !canSave ? 0.5 : 1,
              fontFamily: 'inherit'
            }}
          >
            {busy === 'save-knowledge' ? 'Saving…' : isNew ? 'Create' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
